/**
 * WLED Discovery Service
 * Handles discovering WLED devices on the local network
 * 
 * Discovery methods:
 * 1. mDNS: WLED devices advertise as _wled._tcp.local
 * 2. Scan: Scan network for devices with open HTTP port
 * 3. AP Mode: Detect WLED AP networks for initial setup
 */

import { Platform } from '@/lib/platform'
import { wledLogger, wledDeviceService, type WledDeviceInfo } from './WledDeviceService'
import { httpGet } from '@/lib/http'

/**
 * Discovered WLED device
 */
export interface DiscoveredWledDevice {
  // Network info
  ipAddress: string
  macAddress: string
  
  // Device info
  name: string
  version: string
  ledCount: number
  arch: string  // esp8266, esp32
  
  // Discovery metadata
  discoveryMethod: 'mdns' | 'scan' | 'ap' | 'manual'
  lastSeen: number
  
  // WiFi info (if connected to network)
  wifiSignal?: number
  wifiChannel?: number
  
  // AP mode info (if device is in AP mode)
  isApMode?: boolean
  apSsid?: string
}

/**
 * WiFi network detected on the system (for AP discovery)
 */
export interface DetectedWifiNetwork {
  ssid: string
  bssid: string
  signalStrength: number
  isWled: boolean
}

export interface WledDiscoveryOptions {
  timeout?: number
  scanSubnet?: string  // e.g., "192.168.1" to scan 192.168.1.1-254
  scanPorts?: number[]
}

class WledDiscoveryServiceClass {
  private static readonly DEFAULT_TIMEOUT = 10000
  private static readonly WLED_AP_PREFIXES = ['WLED-', 'WLED_']
  
  /**
   * Discover WLED devices using all available methods
   */
  async discoverDevices(options: WledDiscoveryOptions = {}): Promise<DiscoveredWledDevice[]> {
    // Only works in Tauri (needs local network access)
    if (!Platform.isTauri()) {
      wledLogger.warn('WLED discovery only available in Tauri app')
      return []
    }

    const {
      timeout = WledDiscoveryServiceClass.DEFAULT_TIMEOUT
    } = options

    const discoveries: Promise<DiscoveredWledDevice[]>[] = []

    // Try mDNS discovery
    discoveries.push(this.discoverViaMDNS(timeout))

    // Also try network scanning
    discoveries.push(this.discoverViaNetworkScan(options.scanSubnet, timeout))

    try {
      const results = await Promise.allSettled(discoveries)
      const devices: DiscoveredWledDevice[] = []

      for (const result of results) {
        if (result.status === 'fulfilled') {
          devices.push(...result.value)
        } else {
          wledLogger.warn('Discovery method failed:', result.reason)
        }
      }

      // Deduplicate by MAC address
      const uniqueDevices = this.deduplicateDevices(devices)
      
      wledLogger.info(`Discovered ${uniqueDevices.length} WLED devices`)
      return uniqueDevices
    } catch (error) {
      wledLogger.error('Discovery failed:', error)
      return []
    }
  }

  /**
   * Discover WLED devices via mDNS
   * WLED advertises as _wled._tcp.local and _http._tcp.local
   */
  private async discoverViaMDNS(timeout: number): Promise<DiscoveredWledDevice[]> {
    if (!Platform.isTauri()) {
      return []
    }

    try {
      wledLogger.debug('Starting mDNS discovery for WLED devices...')

      // Use Tauri's mDNS resolver if available
      // For now, we'll use the WLED discovery endpoint as a fallback
      // WLED devices announce themselves and can find other WLED devices
      
      // Attempt to use native mDNS via Tauri plugin (if available)
      try {
        const { invoke } = await import('@tauri-apps/api/core')
        
        // Try to invoke a custom Tauri command for mDNS discovery
        // This would need to be implemented in Rust
        const mdnsResults = await invoke<Array<{ip: string, name: string, mac?: string}>>('discover_mdns_services', {
          serviceType: '_wled._tcp.local',
          timeout: timeout
        }).catch(() => null)

        if (mdnsResults && mdnsResults.length > 0) {
          wledLogger.info(`mDNS found ${mdnsResults.length} WLED services`)
          
          const devices: DiscoveredWledDevice[] = []
          for (const result of mdnsResults) {
            const info = await wledDeviceService.getDeviceInfo(result.ip)
            if (info) {
              devices.push(this.infoToDiscoveredDevice(result.ip, info, 'mdns'))
            }
          }
          return devices
        }
      } catch {
        // mDNS Tauri command not available, fall through to HTTP-based discovery
      }

      // Fallback: Try to find WLED devices by checking common local IPs
      // This is a less efficient but more compatible approach
      wledLogger.debug('mDNS not available, using fallback discovery')
      return []
    } catch (error) {
      wledLogger.error('mDNS discovery error:', error)
      return []
    }
  }

  /**
   * Discover WLED devices by scanning a subnet
   * Scans for devices with HTTP port open and checks if they're WLED
   */
  async discoverViaNetworkScan(
    subnet?: string,
    timeout: number = 5000
  ): Promise<DiscoveredWledDevice[]> {
    if (!Platform.isTauri()) {
      return []
    }

    wledLogger.info('Starting network scan for WLED devices...')

    // Get subnet to scan (use provided or try to detect)
    const targetSubnet = subnet || await this.detectLocalSubnet()
    if (!targetSubnet) {
      wledLogger.warn('Could not determine subnet for scanning')
      return []
    }

    wledLogger.info(`Scanning subnet ${targetSubnet}.* for WLED devices...`)

    const devices: DiscoveredWledDevice[] = []
    
    // Scan common DHCP range where most devices are assigned
    // Home routers typically assign in .2-.200 range
    const ipsToScan = Array.from({ length: 253 }, (_, i) => `${targetSubnet}.${i + 1}`)
    
    wledLogger.info(`Scanning ${ipsToScan.length} IPs (with 1s timeout each)...`)

    // Run all scans in parallel - the 1 second timeout ensures this completes quickly
    const scanPromises = ipsToScan.map(async (ip) => {
      try {
        const info = await wledDeviceService.quickProbe(ip)
        if (info) {
          wledLogger.info(`Found WLED device at ${ip}: ${info.name}`)
          devices.push(this.infoToDiscoveredDevice(ip, info, 'scan'))
        }
      } catch {
        // Device doesn't respond or isn't WLED, ignore
      }
    })
    
    // Add a master timeout of 15 seconds to prevent hanging
    const timeoutPromise = new Promise<void>((resolve) => {
      setTimeout(() => {
        wledLogger.warn('Network scan timeout reached after 15s')
        resolve()
      }, 15000)
    })
    
    await Promise.race([
      Promise.allSettled(scanPromises),
      timeoutPromise
    ])

    wledLogger.info(`Network scan complete. Found ${devices.length} WLED devices`)
    return devices
  }

  /**
   * Check if a device at a given IP is a WLED device and return its info
   */
  async probeDevice(ipAddress: string): Promise<DiscoveredWledDevice | null> {
    try {
      const info = await wledDeviceService.getDeviceInfo(ipAddress)
      if (info) {
        return this.infoToDiscoveredDevice(ipAddress, info, 'manual')
      }
      return null
    } catch {
      return null
    }
  }

  /**
   * Detect WLED access points (devices in AP mode)
   * These are new WLED devices that haven't been configured yet
   */
  async detectWledAccessPoints(): Promise<DetectedWifiNetwork[]> {
    if (!Platform.hasCapability('wifiScan')) {
      wledLogger.warn('WiFi AP scanning only available on desktop (macOS/Windows/Linux)')
      return []
    }

    try {
      const { invoke } = await import('@tauri-apps/api/core')
      
      wledLogger.info('Scanning for WiFi networks...')
      const networks = await invoke<Array<{ssid: string, bssid: string, signal: number}>>('scan_wifi_networks')
      
      wledLogger.info(`Found ${networks.length} WiFi networks`)
      
      const allNetworks = networks.map(network => ({
        ssid: network.ssid,
        bssid: network.bssid,
        signalStrength: network.signal,
        isWled: WledDiscoveryServiceClass.WLED_AP_PREFIXES.some(
          prefix => network.ssid.toUpperCase().startsWith(prefix.toUpperCase())
        )
      }))
      
      const wledNetworks = allNetworks.filter(network => network.isWled)
      wledLogger.info(`Found ${wledNetworks.length} WLED AP networks:`, wledNetworks.map(n => n.ssid))
      
      return wledNetworks
    } catch (error) {
      wledLogger.error('WiFi scanning failed:', error)
      throw error // Re-throw so the UI can show the error
    }
  }

  /**
   * Convert WLED device info to discovered device format
   */
  private infoToDiscoveredDevice(
    ipAddress: string,
    info: WledDeviceInfo,
    method: 'mdns' | 'scan' | 'ap' | 'manual'
  ): DiscoveredWledDevice {
    return {
      ipAddress,
      macAddress: info.mac,
      name: info.name,
      version: info.ver,
      ledCount: info.leds.count,
      arch: info.arch,
      discoveryMethod: method,
      lastSeen: Date.now(),
      wifiSignal: info.wifi?.signal,
      wifiChannel: info.wifi?.channel
    }
  }

  /**
   * Detect the local subnet from the machine's IP
   */
  private async detectLocalSubnet(): Promise<string | null> {
    try {
      const { invoke } = await import('@tauri-apps/api/core')
      
      // Get local IP via Tauri command
      wledLogger.debug('Getting local IP address...')
      const localIp = await invoke<string>('get_local_ip').catch((err) => {
        wledLogger.warn('get_local_ip failed:', err)
        return null
      })
      
      wledLogger.debug('Local IP:', localIp)
      
      if (localIp) {
        const parts = localIp.split('.')
        if (parts.length === 4) {
          const subnet = `${parts[0]}.${parts[1]}.${parts[2]}`
          wledLogger.debug('Detected subnet:', subnet)
          return subnet
        }
      }
      
      return null
    } catch (err) {
      wledLogger.error('detectLocalSubnet error:', err)
      return null
    }
  }

  /**
   * Deduplicate devices by MAC address, preferring most recent
   */
  private deduplicateDevices(devices: DiscoveredWledDevice[]): DiscoveredWledDevice[] {
    const deviceMap = new Map<string, DiscoveredWledDevice>()

    for (const device of devices) {
      const existing = deviceMap.get(device.macAddress)
      if (!existing || device.lastSeen > existing.lastSeen) {
        deviceMap.set(device.macAddress, device)
      }
    }

    return Array.from(deviceMap.values())
  }
}

// Export singleton instance
export const wledDiscoveryService = new WledDiscoveryServiceClass()
